import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Label } from '@/components/ui/label';
import Title from '@/components/ui/Title';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  deleteWebinarResource,
  finalizeWebinarResourceUpload,
  initWebinarResourceUpload,
  listWebinarResources,
} from '@/features/webinar/webinarThunks';
import { useTranslation } from 'react-i18next';
import EmptyInputFile from './EmptyInputFile';
import { Pagination } from '@/components/ui/pagination';
import {
  inspectVideoFile,
  isBroadcastableVideoCodec,
  type VideoCodecInspection,
} from '@/lib/videoCodecValidation';

const PAGE_SIZE = 10;

const RESOURCE_TOTAL_SIZE_EXCEEDED = 'HTTP.ERROR.400.RESOURCE.TOTAL_SIZE.EXCEEDED';
const RESOURCE_FILE_TOO_LARGE = 'HTTP.ERROR.400.RESOURCE.FILE.TOO_LARGE';

function resolveVideoUploadErrorMessage(error: unknown, t: (key: string) => string): string {
  const err = error as {
    message?: string;
    details?: Array<{ message?: string }>;
  };
  const apiMessages = [
    err?.message,
    ...(Array.isArray(err?.details) ? err.details.map((d) => d.message) : []),
  ].filter((m): m is string => typeof m === 'string');

  if (apiMessages.some((m) => m === RESOURCE_TOTAL_SIZE_EXCEEDED)) {
    return t('videosForm.errors.totalSizeExceeded');
  }

  if (apiMessages.some((m) => m === RESOURCE_FILE_TOO_LARGE)) {
    return t('videosForm.errors.fileTooLarge');
  }

  const fallback = err?.message;
  if (!fallback) {
    return t('videosForm.errors.unexpectedUploadError');
  }
  if (fallback.startsWith('HTTP.') || fallback.startsWith('errors.')) {
    const translated = t(fallback);
    return translated === fallback ? t('videosForm.errors.unexpectedUploadError') : translated;
  }
  return t(fallback);
}

const bytesToMB = (bytes: number): string => {
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

export default function VideosForm({
  webinarId,
  workspaceId,
  isEdit,
}: {
  webinarId: number;
  workspaceId: number;
  isEdit?: boolean;
}) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();

  const formSchema = z.object({
    video: z
      .instanceof(File)
      .refine((file) => file.size <= 100 * 1024 * 1024, {
        message: t('videosForm.validation.fileSizeTooLarge'),
      })
      .refine(
        (file) => {
          const allowedTypes = ['video/mp4', 'video/quicktime'];
          return allowedTypes.includes(file.type);
        },
        {
          message: t('videosForm.validation.unsupportedVideoFormat'),
        },
      ),
  });
  const {
    webinarResources,
    webinarResourcesTotalElements,
    webinarResourcesTotalPages,
    webinarResourcesLoading,
    webinarResourceUploadInitLoading,
    webinarResourceFinalizeLoading,
    webinarResourcesError,
  } = useAppSelector((state: RootState) => state.webinar);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      video: undefined,
    },
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string>('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [webinarId]);

  useEffect(() => {
    void dispatch(listWebinarResources({ workspaceId, webinarId, page, size: PAGE_SIZE }));
  }, [dispatch, webinarId, workspaceId, page]);

  // useEffect(() => {
  //   if (webinarResources && webinarResources.length > 0) {
  //     setAccordionValue('');
  //   } else {
  //     setAccordionValue(undefined);
  //   }
  // }, [webinarResources]);

  const uploadFileToUrl = async (url: string, file: File) => {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'Cache-Control': 'public, max-age=31536000, immutable',
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(t('videosForm.errors.uploadFailed'));
    }
  };

  const handleSubmit = async (data: z.infer<typeof formSchema>) => {
    const { video } = data;
    if (!video) {
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      const uploadInit = await dispatch(
        initWebinarResourceUpload({
          workspaceId,
          webinarId,
          input: {
            fileName: video.name,
            mimeType: video.type,
            sizeBytes: video.size,
          },
        }),
      ).unwrap();

      await uploadFileToUrl(uploadInit.uploadUrl, video);

      await dispatch(
        finalizeWebinarResourceUpload({
          workspaceId,
          webinarId,
          input: {
            resourceKey: uploadInit.resourceKey,
            fileName: video.name,
            mimeType: video.type,
            sizeBytes: video.size,
            pageCount: null,
            checksumSha256: null,
            position: null,
          },
        }),
      ).unwrap();

      setSelectedFile(null);
      form.reset();

      const targetPage = 0;
      if (page !== targetPage) {
        setPage(targetPage);
      } else {
        await dispatch(
          listWebinarResources({ workspaceId, webinarId, page: targetPage, size: PAGE_SIZE }),
        ).unwrap();
      }
    } catch (error) {
      setUploadError(resolveVideoUploadErrorMessage(error, t));
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadError(null);
    setIsUploading(true);
    let inspection: VideoCodecInspection;
    try {
      inspection = await inspectVideoFile(file);
    } catch {
      inspection = { status: 'unknown' };
    } finally {
      setIsUploading(false);
    }

    if (!isBroadcastableVideoCodec(inspection)) {
      e.target.value = '';
      setSelectedFile(null);
      form.resetField('video');
      setUploadError(t('videosForm.errors.videoCodecNotBroadcastable'));
      return;
    }

    setSelectedFile(file);
    form.setValue('video', file);
    void handleSubmit({ video: file });
  };

  const handleDeleteVideo = async (resourceId: number) => {
    setUploadError(null);
    const isLastItemOnPage = webinarResources.length === 1;
    await dispatch(deleteWebinarResource({ workspaceId, webinarId, resourceId }));
    if (isLastItemOnPage && page > 0) {
      setPage(page - 1);
    } else {
      void dispatch(listWebinarResources({ workspaceId, webinarId, page, size: PAGE_SIZE }));
    }
  };

  if (webinarResourcesLoading && !webinarResources?.length) {
    return (
      <div>
        <Accordion type="single" collapsible defaultValue="item-1">
          <AccordionItem
            value="item-1"
            className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
          >
            <AccordionTrigger className=" rounded-[20px]">
              <Title level={2}>{t('videosForm.title')}</Title>
            </AccordionTrigger>
            <AccordionContent
              className={`flex flex-col gap-4 rounded-[20px] bg-white pt-4 p-0 ${isEdit ? 'pb-4' : ''}`}
            >
              <div className="space-y-4">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div className="pt-4">
          <Skeleton className="h-10 w-40" />
        </div>
      </div>
    );
  }

  const isButtonLoading =
    isUploading || webinarResourceUploadInitLoading || webinarResourceFinalizeLoading;

  const totalPages = Math.max(
    1,
    webinarResourcesTotalPages,
    Math.ceil(webinarResourcesTotalElements / PAGE_SIZE),
  );
  const isListLoading = webinarResourcesLoading && webinarResourcesTotalElements > 0;

  return (
    <div>
      <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem value="item-1" className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}>
          <AccordionTrigger
            fillForm={webinarResourcesTotalElements > 0}
            className=" rounded-[20px]"
          >
            <div>
              <Title level={2}>{t('videosForm.title')}</Title>
              <p className="text-sm font-normal text-tertiary">{t('videosForm.description')}</p>
            </div>
          </AccordionTrigger>
          <AccordionContent
            className={`flex flex-col gap-4 rounded-[20px] bg-white pt-4 p-0 ${isEdit ? 'pb-4 bg-subtitle' : ''}`}
          >
            <div className="space-y-[10px]">
              {webinarResourcesTotalElements === 0 ? (
                <button
                  type="button"
                  className="w-full"
                  aria-label={t('videosForm.addVideo')}
                  onClick={() => document.getElementById('video-upload')?.click()}
                >
                  <EmptyInputFile
                    contentType={t('videosForm.emptyContentType')}
                    type="video"
                    isLoading={isButtonLoading}
                  />
                </button>
              ) : isListLoading ? (
                <div className="flex flex-col items-center justify-center gap-3 py-10">
                  <Loader2 className="size-8 animate-spin text-primary" />
                </div>
              ) : (
                webinarResources.map((video) => (
                  <div key={video.id}>
                    <Label className="p-4 bg-white border border-medium rounded-2xl flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="size-10 bg-medium rounded-lg flex items-center justify-center">
                          <img src="/assets/attach.svg" alt={t('videosForm.attachIcon')} />
                        </div>
                        <div className="flex flex-col gap-2">
                          <span>{video.fileName}</span>
                          <p className="text-sm text-gray-tertiary">{bytesToMB(video.sizeBytes)}</p>
                        </div>
                      </div>
                      <Trash2
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteVideo(video.id);
                        }}
                      />
                    </Label>
                  </div>
                ))
              )}
              {totalPages > 1 && (
                <div className="pt-4 flex justify-center">
                  <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                </div>
              )}
            </div>

            <input
              type="file"
              id="video-upload"
              className="sr-only"
              accept="video/mp4,video/quicktime,.mov,.mp4"
              onChange={handleFileChange}
              disabled={isButtonLoading}
            />
            {webinarResourcesTotalElements > 0 && (
              <div className="pt-4">
                <button
                  type="button"
                  className="flex items-center gap-2"
                  onClick={() => document.getElementById('video-upload')?.click()}
                  disabled={isButtonLoading}
                >
                  {isButtonLoading ? (
                    <Loader2 className="size-6 animate-spin" />
                  ) : (
                    <Plus className="size-6" />
                  )}
                  <Label className="cursor-pointer">
                    {isButtonLoading ? t('videosForm.uploading') : t('videosForm.addVideo')}
                  </Label>
                </button>
                {(uploadError || webinarResourcesError) && (
                  <p className="text-sm text-error mt-2">{uploadError ?? webinarResourcesError}</p>
                )}
                {selectedFile && !isButtonLoading && (
                  <p className="text-sm text-tertiary mt-2">
                    {t('videosForm.selectedFile')} {selectedFile.name}
                  </p>
                )}
              </div>
            )}
            {(uploadError || webinarResourcesError) && webinarResourcesTotalElements === 0 && (
              <p className="text-sm text-error mt-2">{uploadError ?? webinarResourcesError}</p>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
